<script setup>
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import SelectInput from "@/Components/SelectInput.vue";
import { Inertia } from "@inertiajs/inertia";
import { useHelpers } from "@/helpers";
import { usePage } from "@inertiajs/inertia-vue3";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import { ref, watch, computed } from "vue";
import _ from "lodash";
import SearchSelect from "@/Components/SearchSelect.vue";

const helpers = useHelpers();
const currentUser = usePage().props.value.auth.user;

const props = defineProps({
  garments: Array,
  filters: Object,
  sorting: Object,
  departments: Array,
  fabrics: Array,
  clients: Array,
  sizeScales: Array,
  colors: Array,
  locations: Array,
});

const clientNameRef = ref(null);
const colorRef = ref(null);
const departmentRef = ref(null);
const sizeRef = ref(null);
const nameRef = ref(null);

const defaultFilters = {
  client: null,
  garment: null,
  color: null,
  name: null,
  department: null,
  size: null,
  fabric: null,
  location: null,
  detail_description: null,
};

const filterValues = ref({ ...defaultFilters, ...(props.filters || {}) });
const searching = ref(false);

const compactFilters = (obj) => {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v !== null && v !== undefined && String(v).trim() !== "") {
      out[k] = v;
    }
  });
  return out;
};

const resetFilters = () => {
  searching.value = true;
  filterValues.value = { ...defaultFilters };
  const queryParameters = route().params || {};
  if (queryParameters.page) delete queryParameters.page;
  queryParameters.filters = {};
  queryParameters.sorting = {
    column: props.sorting?.column || 'modifieddate',
    is_ascending: props.sorting?.is_ascending || false,
  };

  try {
    localStorage.setItem('samples.filters', JSON.stringify(queryParameters.filters));
    localStorage.setItem('samples.sorting', JSON.stringify(queryParameters.sorting));
  } catch (e) { }
  Inertia.get(route(route().current(), queryParameters), {}, {
    preserveScroll: true,
    preserveState: true,
    onFinish: () => { searching.value = false; }
  });
};

const makeSearchRequest = _.debounce(() => executeSearchRequest(), 400);

const executeSearchRequest = () => {
  searching.value = true;
  const queryParameters = route().params || {};
  if (queryParameters.page) {
    delete queryParameters.page;
  }
  queryParameters['filters'] = compactFilters(filterValues.value);
  queryParameters['sorting'] = {
    column: props.sorting?.column || 'modifieddate',
    is_ascending: props.sorting?.is_ascending || false,
  };

  try {
    localStorage.setItem('samples.filters', JSON.stringify(queryParameters['filters']));
    localStorage.setItem('samples.sorting', JSON.stringify(queryParameters['sorting']));
  } catch (e) { }

  Inertia.get(route(route().current(), queryParameters), {}, {
    preserveScroll: true,
    preserveState: true,
    onFinish() {
      searching.value = false;
    }
  });
};

watch(() => props.sorting, () => executeSearchRequest(), { deep: true });

const clientOptions = computed(() => {
  const seen = new Set();
  const out = [];
  for (const c of props.clients || []) {
    const label = c?.client ?? "";
    if (!label || seen.has(label)) continue;
    seen.add(label);
    out.push({ label, value: label });
  }
  return out;
});

const clientModel = computed({
  get: () => (filterValues.value.client ? String(filterValues.value.client) : null),
  set: (v) => { filterValues.value.client = v || null; },
});
</script>

<template>
  <div>
    <InputLabel value="Sample Name" />
    <TextInput id="name" type="text" class="mt-1 w-full" placeholder="Sample Name" ref="nameRef"
      v-model="filterValues.name" @keyup="makeSearchRequest" />
  </div>

  <div>
    <InputLabel value="Garment Description" />
    <TextInput id="detail_description" type="text" class="mt-1 w-full" placeholder="Garment Description"
      v-model="filterValues.detail_description" @keyup="makeSearchRequest" />
  </div>

  <div>
    <InputLabel value="Location" />
    <SelectInput id="location" class="mt-1"
      v-model="filterValues.location" @change="makeSearchRequest" :disabled="searching">
      <option :value="null">Please select</option>
      <option v-for="location in locations" :key="location.locationId" :value="location.locationId">
        {{ location.name }}
      </option>
    </SelectInput>
  </div>

  <!--<div>
    <InputLabel value="Client Name"/>
    <select id="client"
            class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full p-2.5"
            v-model="filterValues.client"
            @change="makeSearchRequest"
            :disabled="searching"
    >
      <option :value="null">Please select</option>
      <option v-for="client in clients" :key="client.ID" :value="client.client">
        {{ client.client }}
      </option>
    </select>
  </div>-->
  <!-- Client Name -->
  <div>
    <InputLabel value="Client Name" />
    <SearchSelect v-model="clientModel" :options="clientOptions" placeholder="Please select" :disabled="searching"
      @change="makeSearchRequest" />
  </div>

  <div>
    <InputLabel value="Color" />
    <SelectInput id="color" class="mt-1"
      v-model="filterValues.color" @change="makeSearchRequest" :disabled="searching">
      <option :value="null">Please select</option>
      <option v-for="color in colors" :key="color.colorID" :value="color.colorID">
        {{ color.colorName }}
      </option>
    </SelectInput>
  </div>

  <div>
    <InputLabel value="Garment" />
    <SelectInput id="garment" class="mt-1"
      v-model="filterValues.garment" @change="makeSearchRequest" :disabled="searching">
      <option :value="null">Please select</option>
      <option v-for="garment in garments" :key="garment.garmentID" :value="garment.garmentID">
        {{ garment.garmentName }}
      </option>
    </SelectInput>
  </div>

  <div>
    <InputLabel value="Department" />
    <SelectInput id="department" class="mt-1"
      v-model="filterValues.department" @change="makeSearchRequest" :disabled="searching">
      <option :value="null">Please select</option>
      <option v-for="department in departments" :key="department.id || department.department" :value="department.id">
        {{ department.department }}
      </option>
    </SelectInput>
  </div>

  <div>
    <InputLabel value="Material" />
    <SelectInput id="fabric" class="mt-1"
      v-model="filterValues.fabric" @change="makeSearchRequest" :disabled="searching">
      <option :value="null">Please select</option>
      <option v-for="fabric in fabrics" :key="fabric.fabricID || fabric.fabName" :value="fabric.fabricID">
        {{ fabric.fabName }}
      </option>
    </SelectInput>
  </div>

  <div>
    <InputLabel value="Reset Filter" class="text-gray-100" />
    <SecondaryButton @click="resetFilters" class="mt-1 h-[42px]">Reset Filters</SecondaryButton>
  </div>
</template>
